import { Injectable, EffectRef } from '@angular/core';
import { BaseNotifier } from '../../shared/notifiers/base-notifier';
import { Ref } from '../../shared/providers/ref';
import { MapState } from '../states/map-state';
import { MapRepositoryProvider } from '../../shared/providers/repository-providers';
import { ViewMode } from '../../shared/enums/view-mode.enum';

import ArcGISMap from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import Ground from '@arcgis/core/Ground';
import ElevationLayer from '@arcgis/core/layers/ElevationLayer';
import WebTileLayer from '@arcgis/core/layers/WebTileLayer';

import Basemap from '@arcgis/core/Basemap';
import { ViewModeProvider } from '../providers/view-mode-provider';
import { BasemapProvider } from '../providers/basemap-provider';

@Injectable({ providedIn: 'root' })
export class MapNotifier extends BaseNotifier<MapState> {
  private _map: ArcGISMap | null = null;
  private _mapView: MapView | null = null;
  private _sceneView: SceneView | null = null;
  private _view: MapView | SceneView | null = null;
  private mapReady = false;
  private subs: EffectRef[] = [];

  constructor(ref: Ref) {
    super(ref, { visibleCount: 0, view: null, map: null, viewRotation: 0 });
  }

  isReady(): boolean {
    return this.mapReady;
  }

  async initViews(container2d: HTMLDivElement, container3d: HTMLDivElement): Promise<void> {
    const mapRepo = this.ref.read(MapRepositoryProvider).instance;
    const r = await mapRepo.load();
    if (!r.success) return;
    const d = r.data!;

    const map = new ArcGISMap({
      ground: new Ground({
        layers: [new ElevationLayer({ url: d.elevation.url })],
      }),
    });

    const tiles = new WebTileLayer({
      urlTemplate: d.basemap.urlTemplate,
      copyright: d.basemap.copyright,
      subDomains: d.basemap.subDomains.length > 0 ? d.basemap.subDomains : undefined,
    });
    map.basemap = new Basemap({
      baseLayers: [tiles],
      id: d.basemap.id,
      title: d.basemap.title,
    });

    this._map = map;

    this._mapView = new MapView({
      container: container2d,
      map,
      center: d.center,
      zoom: d.zoom,
    });
    this._mapView.popup!.dockEnabled = false;
    this._mapView.ui.components = [];

    this._sceneView = new SceneView({
      container: container3d,
      map,
      center: d.center,
      zoom: d.zoom,
      qualityProfile: d.scene.qualityProfile as any,
      environment: {
        atmosphere: { quality: d.scene.atmosphereQuality } as any,
        starsEnabled: d.scene.starsEnabled,
        lighting: {
          directShadowsEnabled: d.scene.lighting.directShadowsEnabled,
          date: new Date(d.scene.lighting.date),
        } as any,
      } as any,
    });
    this._sceneView.popup!.dockEnabled = false;
    this._sceneView.ui.components = [];

    await this._mapView.when();
    this._view = this._mapView;
    this.updateState(s => ({ ...s, view: this._mapView, map }));
    this.mapReady = true;

    const basemapProvider = this.ref.read(BasemapProvider);
    if (basemapProvider.state().options.length > 0) {
      basemapProvider.notifier.setActive(basemapProvider.state().activeId);
    }

    this.subs.forEach(s => s.destroy());
    this.subs = [
      this.ref.createEffect(() => {
        const mode = this.ref.read(ViewModeProvider).state().mode;
        if (this.mapReady) this.switchTo(mode);
      }),
    ];

    this._sceneView.when().catch(() => {});
  }

  getActiveView(): MapView | SceneView | null {
    return this._view;
  }

  async switchTo(mode: ViewMode): Promise<void> {
    if (!this._mapView || !this._sceneView) return;

    const newView = mode === ViewMode.Map ? this._mapView : this._sceneView;
    if (this._view === newView) return;

    const oldView = this._view;
    this._view = newView;
    this.updateState(s => ({ ...s, view: newView }));

    if (oldView) {
      const center = (oldView as any).center ?? oldView.extent?.center;
      const lat = center?.latitude ?? 0;
      const factor = Math.cos((lat * Math.PI) / 180);

      if (mode === ViewMode.Scene) {
        await (newView as SceneView).goTo({
          target: center,
          tilt: 55,
          heading: (oldView as MapView).rotation ?? 0,
          scale: oldView.scale * factor,
        }, { animate: true, duration: 800 });
      } else {
        (newView as MapView).goTo({
          target: center,
          scale: oldView.scale / factor,
        });
      }
    }
  }

  async persistPosition(): Promise<void> {
    const v = this._view;
    if (!v) return;
    const c = (v as any).center;
    if (!c) return;
    const mapRepo = this.ref.read(MapRepositoryProvider).instance;
    const current = await mapRepo.load();
    if (!current.success || !current.data) return;
    await mapRepo.save({
      ...current.data,
      center: [c.longitude ?? 0, c.latitude ?? 0],
      zoom: (v as any).zoom ?? 7,
      rotation: (v as any).rotation ?? 0,
    });
  }

  getMapView(): MapView | null { return this._mapView; }
  getSceneView(): SceneView | null { return this._sceneView; }
}
